import React, { useState, useEffect, useRef } from 'react';
import './Ourteam.css'; // Ensure CSS path is correct



// --- Placeholder Icons (MUST REPLACE) ---
const PlaceholderIcon = ({ children, className = "" }) => <span className={`team-icon-placeholder ${className}`}>{children}</span>;
const LinkedInIcon = () => <PlaceholderIcon className="icon-linkedin">in</PlaceholderIcon>;
const TwitterIcon = () => <PlaceholderIcon className="icon-twitter">t</PlaceholderIcon>;
const EmailIcon = () => <PlaceholderIcon className="icon-email">@</PlaceholderIcon>;
const CloseIcon = () => <PlaceholderIcon className="icon-close">✕</PlaceholderIcon>; // Placeholder for close button

// --- Reusable Intersection Observer Hook ---
const useVisibility = (options) => {
    const [isVisible, setIsVisible] = useState(false);
    const elementRef = useRef(null);
    const observerRef = useRef(null);

    useEffect(() => {
        const currentElement = elementRef.current;
        if (!currentElement) return;
        observerRef.current = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    if (options?.triggerOnce && observerRef.current) {
                        observerRef.current.unobserve(currentElement);
                    }
                } else {
                    if (options && !options.triggerOnce) {
                        setIsVisible(false);
                    }
                }
            });
        }, options);
        observerRef.current.observe(currentElement);
        return () => { if (observerRef.current && currentElement) observerRef.current.unobserve(currentElement); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options?.threshold, options?.rootMargin, options?.triggerOnce]);

    return [elementRef, isVisible];
};

// --- Helper for character animation ---
const CharAnimator = ({ text, baseDelay = 0, stagger = 0.025 }) => {
    if (!text || typeof text !== 'string' || text.length === 0) return null;
    return text.split('').map((char, index) => (
        <span key={index} className="team-char-reveal" style={{ '--char-delay': `${baseDelay + index * stagger}s` }} aria-hidden="true">
            {char === ' ' ? '\u00A0' : char}
        </span>
    ));
};

// --- Team Member Data (Add a longer 'fullBio' for modal) ---
const teamMembers = [
    { id: 1, name: "Mr. Mukesh Agarwal", title: "regional manager", bio: "Visionary leader driving healthcare innovation.", fullBio: "With over two decades shaping healthcare strategy,  integrating technology and compassionate care to redefine industry standards and foster sustainable growth.",  social: { linkedin: "#", email: "#" } },
    { id: 2, name: "Mr. Bharat", title: "Chief Operations Officer", bio: "Expert strategist optimizing operations efficiently.", fullBio: "Mr. Bharat excels in streamlining complex workflows and implementing data-driven operational improvements. His focus on efficiency ensures seamless service delivery and optimal resource allocation.",  social: { linkedin: "#", twitter: "#" } },
    { id: 3, name: "Ms. Aisha", title: "Head of Talent Acquisition", bio: "Dedicated to sourcing top-tier professionals.", fullBio: "Aisha possesses an unparalleled knack for identifying and attracting exceptional healthcare talent. She leads our recruitment efforts with a focus on cultural fit and long-term success for both candidates and clients.",  social: { linkedin: "#", email: "#" } },
    { id: 4, name: "Mr. Ramesh ", title: "Director of Technology", bio: "Innovating with cutting-edge tech solutions.", fullBio: "Mr. Ramesh spearheads our technological advancements, developing and implementing platforms that enhance connectivity, improve data security, and provide intuitive user experiences for our partners.", social: { linkedin: "#" } },
    { id: 5, name: "Mr. Rudra", title: "Clinical Quality Lead", bio: "Ensuring the highest standards of excellence.", fullBio: "Mr. Rudra oversees all clinical quality assurance programs. Her rigorous approach guarantees that our placed professionals adhere to the highest standards of patient safety and clinical best practices.",  social: { linkedin: "#", email: "#" } },
    { id: 6, name: "Mr. Jayesh", title: "Client Relations Manager", bio: "Building strong, lasting facility partnerships.", fullBio: "Mr. Jayesh is dedicated to understanding the unique needs of each client, fostering strong communication, and ensuring lasting partnerships built on trust and exceptional service.", social: { linkedin: "#", twitter: "#" } },
];

// =================================================
// === TeamMemberModal Component Definition ========
// =================================================
function TeamMemberModal({ member, onClose, isOpen }) {
    const modalContentRef = useRef(null);

    // Close modal on Escape key press
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            // Focus management (basic example)
            setTimeout(() => modalContentRef.current?.focus(), 300); // Delay focus slightly
        } else {
            document.removeEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    // Prevent clicks inside content from closing modal
    const handleContentClick = (e) => {
        e.stopPropagation();
    };

    if (!member) return null; // Don't render if no member selected

    return (
        <div
            className={`team-member-modal-overlay ${isOpen ? 'modal-is-open' : ''}`}
            onClick={onClose} // Close on overlay click
            role="dialog"
            aria-modal="true"
            aria-labelledby={`modal-title-${member.id}`}
            aria-describedby={`modal-desc-${member.id}`}
        >
            <div
                ref={modalContentRef}
                className="team-member-modal-content"
                onClick={handleContentClick}
                tabIndex="-1" // Make it focusable
            >
                {/* Close Button */}
                <button
                    className="team-member-modal-close-btn"
                    onClick={onClose}
                    aria-label="Close member details"
                >
                    <CloseIcon />
                </button>

                 {/* Content Layout */}
              
                     
                     <div className="team-modal-info-container">
                        <h2 id={`modal-title-${member.id}`} className="team-modal-name">{member.name}</h2>
                        <p className="team-modal-title">{member.title}</p>
                        <div className="team-modal-divider"></div>
                        <p id={`modal-desc-${member.id}`} className="team-modal-full-bio">
                            {member.fullBio || member.bio} {/* Use fullBio if available */}
                        </p>
                         {/* Social Links inside Modal */}
                         <div className="team-modal-social-links">
                            {member.social?.linkedin && <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><LinkedInIcon /></a>}
                            {member.social?.twitter && <a href={member.social.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter"><TwitterIcon /></a>}
                            {member.social?.email && <a href={member.social.email} aria-label="Email"><EmailIcon /></a>}
                        </div>
                    
                 </div>
            </div>
        </div>
    );
}


// ================================================
// === TeamCard Component Definition (Internal) ===
// ================================================
function TeamCard({ member, index, isGridVisible, onCardClick }) { // Added onCardClick prop
    const cardInnerRef = useRef(null);

    // Mouse Interaction Logic
    const handleMouseMove = (e) => {
        const cardInner = cardInnerRef.current;
        if (!cardInner || !isGridVisible) return;
        const rect = cardInner.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        const rotateY = (x / (rect.width / 2)) * 8;
        const rotateX = (y / (rect.height / 2)) * -8;
        const glowX = (e.clientX - rect.left) / rect.width * 100;
        const glowY = (e.clientY - rect.top) / rect.height * 100;

        requestAnimationFrame(() => {
            cardInner.style.setProperty('--rotateX', `${rotateX}deg`);
            cardInner.style.setProperty('--rotateY', `${rotateY}deg`);
            cardInner.style.setProperty('--glow-x', `${glowX}%`);
            cardInner.style.setProperty('--glow-y', `${glowY}%`);
        });
    };

    const handleMouseLeave = () => {
        const cardInner = cardInnerRef.current;
        if (!cardInner) return;
        requestAnimationFrame(() => {
            cardInner.style.setProperty('--rotateX', '0deg');
            cardInner.style.setProperty('--rotateY', '0deg');
        });
    };

    const cardIsReadyToAnimate = isGridVisible;
    const cardClass = `team-card ${cardIsReadyToAnimate ? 'animate-in' : ''}`;

    return (
        // Add onClick handler to the outer div
        <div
            className={cardClass}
            style={{ '--card-delay': `${index * 0.1}s` }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={() => onCardClick(member)} // Trigger modal open
            role="button" // Indicate interactivity
            tabIndex="0" // Make it keyboard accessible
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onCardClick(member)} // Trigger on key press
        >
            <div ref={cardInnerRef} className="team-card-inner">
                {/* Keep all existing card visual elements */}
                <div className="team-card-border-animated">
                    {/* Inline SVG definition for border */}
                     <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                         <defs>
                             <linearGradient id={`borderGradientApex-${member.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="var(--tp-sky-blue)" stopOpacity="0.8"/>
                                <stop offset="100%" stopColor="var(--tp-soft-green)" stopOpacity="0.8"/>
                            </linearGradient>
                        </defs>
                         <path d="M 10 0 L 90 0 Q 100 0 100 10 L 100 90 Q 100 100 90 100 L 10 100 Q 0 100 0 90 L 0 10 Q 0 0 10 0 Z" stroke={`url(#borderGradientApex-${member.id})`} strokeWidth="1.5" fill="none" />
                     </svg>
                </div>
                <div className="team-card-bg-texture"></div>
                <div className="team-card-bg-glow"></div>

              
                   
                    <svg className="team-card-image-mask-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                         <defs>
                            <mask id={`team-mask-${member.id}`}>
                                <circle cx="50" cy="50" r="0" fill="white">
                                    {cardIsReadyToAnimate && <animate attributeName="r" values="0; 75" dur="1s" begin={`calc(0.5s + ${index * 0.1}s)`} fill="freeze" calcMode="spline" keyTimes="0; 1" keySplines="0.16, 1, 0.3, 1"/>}
                                </circle>
                             </mask>
                         </defs>
                        <rect width="100" height="100" fill="white" mask={`url(#team-mask-${member.id})`} />
                    </svg>
            
               

                <div className="team-card-content">
                    <div className="team-card-content-limiter">
                        <h3 className="team-card-name" aria-label={member.name}>
                            <CharAnimator text={member.name} baseDelay={0.8 + index * 0.1} stagger={0.04} />
                        </h3>
                        <p className="team-card-title" aria-label={member.title}>
                            <CharAnimator text={member.title} baseDelay={1.0 + index * 0.1} stagger={0.03} />
                        </p>
                        <p className="team-card-bio">
                            {member.bio}
                        </p>
                    </div>
                </div>

                <div className="team-card-social-reveal">
                    <div className="team-card-social-links">
                        {member.social?.linkedin && <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" style={{'--icon-reveal-delay': '0.1s'}}><LinkedInIcon /></a>}
                        {member.social?.twitter && <a href={member.social.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter" style={{'--icon-reveal-delay': '0.2s'}}><TwitterIcon /></a>}
                        {member.social?.email && <a href={member.social.email} aria-label="Email" style={{'--icon-reveal-delay': '0.3s'}}><EmailIcon /></a>}
                    </div>
                 </div>

            </div>
        </div>
    );
}


// =============================================
// === OurTeamPage Component Definition ========
// =============================================
function OurTeamPage() {
    const [headerRef, isHeaderVisible] = useVisibility({ threshold: 0.3, triggerOnce: true });
    const [gridRef, isGridVisible] = useVisibility({ threshold: 0.05, triggerOnce: true });

    // --- Modal State ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);

    // --- Modal Control Functions ---
    const openModal = (member) => {
        setSelectedMember(member);
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden'; // Lock body scroll
    };

    const closeModal = () => {
        setIsModalOpen(false);
        document.body.style.overflow = ''; // Unlock body scroll
        // Delay clearing selected member to allow fade-out animation
        setTimeout(() => setSelectedMember(null), 500); // Match modal transition duration
    };

    return (
        <div className="team-page-container">
            {/* ==== Apex Page Header ==== */}
            <header ref={headerRef} className={`team-page-header ${isHeaderVisible ? 'is-visible' : ''}`}>
                {/* Header content remains the same */}
                 <div className="team-header-bg-canvas"></div>
                 <div className="team-header-light-ray ray-1"></div>
                 <div className="team-header-light-ray ray-2"></div>
                 <div className="team-header-light-ray ray-3"></div>
                <div className="team-page-header-content">
                    <h1 className="team-page-main-title" aria-label="Our Dedicated Team">
                        <CharAnimator text="Our Dedicated Team" baseDelay={0.3} stagger={0.04}/>
                    </h1>
                    <p className="team-page-subtitle">
                        The architects of innovation, commitment, and excellence in healthcare solutions.
                    </p>
                </div>
            </header>

            {/* ==== Apex Team Grid Section ==== */}
            <section ref={gridRef} className={`team-grid-section ${isGridVisible ? 'is-visible' : ''}`}>
                 {/* Grid background remains the same */}
                 <div className="team-grid-bg-shape grid-shape-1"></div>
                 <div className="team-grid-bg-shape grid-shape-2"></div>

                <div className="team-grid-container">
                    {teamMembers.map((member, index) => (
                        // Pass the openModal function to each card
                        <TeamCard
                            key={member.id}
                            member={member}
                            index={index}
                            isGridVisible={isGridVisible}
                            onCardClick={openModal} // <-- Pass the function here
                        />
                    ))}
                </div>
            </section>

            {/* ==== Render Modal Conditionally ==== */}
            <TeamMemberModal
                member={selectedMember}
                isOpen={isModalOpen}
                onClose={closeModal}
            />

        </div> // End team-page-container
    );
}

export default OurTeamPage; // Export the main page component